/**
 * @engine/core - Core Types
 * 
 * Fundamental types for the GameObject/Component architecture.
 */

// ============================================================================
// IDENTIFIERS
// ============================================================================

/**
 * Unique identifier for GameObjects within a scene
 * Uses incremental IDs for runtime, GUIDs for serialization
 */
export type GameObjectId = number;

/**
 * Unique identifier for component instances
 */
export type ComponentId = number;

/**
 * Level identifier (runtime ID)
 * @note Renamed from SceneId
 */
export type LevelId = number;

/**
 * Global Unique Identifier for persistent references
 */
export type GUID = string;

/**
 * Null GUID constant
 */
export const NULL_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * Generate a new GUID
 */
export function generateGUID(): GUID {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ============================================================================
// TAGS & LAYERS
// ============================================================================

/**
 * Tag ID for categorizing GameObjects
 * @note Uses numeric IDs for WASM/MemoryView compatibility
 */
export type Tag = number;

/**
 * Layer for rendering and physics grouping
 */
export type Layer = number;

/**
 * Built-in layers
 */
export const Layers = {
  Default: 0,
  TransparentFX: 1,
  IgnoreRaycast: 2,
  Water: 4,
  UI: 5,
  // User layers start at 8
  User1: 8,
  User2: 9,
  User3: 10,
} as const;

/**
 * Built-in tags
 * @note String names are stored separately in TagRegistry, only IDs used at runtime
 */
export const Tags = {
  Untagged: 0,
  MainCamera: 1,
  Player: 2,
  GameController: 3,
  // User tags start at 8
} as const;

// ============================================================================
// TRANSFORM
// ============================================================================

/**
 * Transform data (position, rotation, scale)
 */
export interface TransformData {
  /** Local position relative to parent */
  localPosition: { x: number; y: number; z: number };
  
  /** Local rotation in degrees */
  localRotation: number;
  
  /** Local scale */
  localScale: { x: number; y: number };
}

/**
 * Default transform data
 */
export const DEFAULT_TRANSFORM: TransformData = {
  localPosition: { x: 0, y: 0, z: 0 },
  localRotation: 0,
  localScale: { x: 1, y: 1 },
};

// ============================================================================
// GAME OBJECT
// ============================================================================

/**
 * Configuration for creating a GameObject
 */
export interface GameObjectConfig {
  /** Persistent GUID (optional - will be generated if not provided) */
  guid?: GUID;
  
  /** Display name */
  name?: string;
  
  /** Tag */
  tag?: Tag;
  
  /** Layer */
  layer?: Layer;
  
  /** Is active */
  active?: boolean;
  
  /** Is static (no transform updates) */
  isStatic?: boolean;
  
  /** Initial transform */
  transform?: Partial<TransformData>;
  
  /** Parent GameObject ID */
  parent?: GameObjectId | null;
}

/**
 * Immutable identity of a GameObject (readonly after creation)
 * Stored separately from mutable state, not mapped to MemoryView
 */
export interface GameObjectIdentity {
  /** Unique runtime ID */
  readonly id: GameObjectId;
  
  /** Persistent GUID for serialization */
  readonly guid: GUID;
  
  /** Scene this object belongs to */
  readonly levelId: LevelId | null;
  
  /** Prefab instance info (if instantiated from prefab) */
  readonly prefabGuid?: GUID;
  
  /** Is static (no transform updates, cannot change at runtime) */
  readonly isStatic: boolean;
}

/**
 * Mutable state of a GameObject
 * Designed for WASM/MemoryView compatibility with fixed-size fields
 * 
 * Memory layout (4 bytes total):
 * - tag:              u8  (offset 0)
 * - layer:            u8  (offset 1)
 * - flags:            u8  (offset 2) - packed booleans
 * - _reserved:        u8  (offset 3) - padding/future use
 * 
 * Flags bit layout:
 * - bit 0: activeSelf
 * - bit 1: activeInHierarchy
 */
export interface GameObjectMutableState {
  /** Tag ID (0-255) */
  tag: Tag;
  
  /** Layer (0-255) */
  layer: Layer;
  
  /** Is active in hierarchy */
  activeSelf: boolean;
  
  /** Is active considering parent hierarchy (computed) */
  activeInHierarchy: boolean;
}

/**
 * String properties of a GameObject
 * Stored separately, not suitable for MemoryView
 */
export interface GameObjectStrings {
  /** Display name */
  name: string;
}

/**
 * Full internal state of a GameObject
 * Combines identity, mutable state, and string properties
 */
export interface GameObjectState extends GameObjectIdentity, GameObjectMutableState, GameObjectStrings {}

// ============================================================================
// COMPONENT 
// ============================================================================

/**
 * Configuration for creating a component
 */
export interface ComponentConfig {
  /** Component type ID (from component registry) */
  typeId: string;
  
  /** Is enabled */
  enabled?: boolean;
  
  /** Initial property values */
  properties?: Record<string, unknown>;
}

/**
 * Internal state of a component
 */
export interface ComponentState {
  /** Unique runtime ID */
  id: ComponentId;
  
  /** Component type ID */
  typeId: string;
  
  /** Is enabled */
  enabled: boolean;
  
  /** Owner GameObject ID */
  gameObjectId: GameObjectId;
  
  /** Script instance ID (for AS components) */
  scriptInstanceId?: string;
  
  /** Component data (serializable properties) */
  data?: Record<string, unknown>;
  
  /** Version counter for dirty tracking (incremented on data changes) */
  version?: number;
}

// ============================================================================
// LIFECYCLE
// ============================================================================

/**
 * Component lifecycle methods
 */
export interface ComponentLifecycle {
  /** Called when component is created */
  onCreate?(): void;
  
  /** Called when component is enabled */
  onEnable?(): void;
  
  /** Called once before first update */
  onStart?(): void;
  
  /** Called every frame */
  onUpdate?(deltaTime: number): void;
  
  /** Called at fixed interval for physics */
  onFixedUpdate?(fixedDeltaTime: number): void;
  
  /** Called after all updates */
  onLateUpdate?(deltaTime: number): void;
  
  /** Called when component is disabled */
  onDisable?(): void;
  
  /** Called when component is destroyed */
  onDestroy?(): void;
}

// ============================================================================
// EVENTS
// ============================================================================

/**
 * Core engine events
 */
export type CoreEventType =
  | 'gameobject:created'
  | 'gameobject:destroyed'
  | 'gameobject:activated'
  | 'gameobject:deactivated'
  | 'gameobject:parentChanged'
  | 'component:added'
  | 'component:removed'
  | 'component:enabled'
  | 'component:disabled'
  | 'level:loaded'
  | 'level:unloaded'
  | 'level:activated';

/**
 * Event payload types
 */
export interface CoreEventPayloads {
  'gameobject:created': { id: GameObjectId; guid: GUID };
  'gameobject:destroyed': { id: GameObjectId; guid: GUID };
  'gameobject:activated': { id: GameObjectId };
  'gameobject:deactivated': { id: GameObjectId };
  'gameobject:parentChanged': { id: GameObjectId; oldParent: GameObjectId | null; newParent: GameObjectId | null };
  'component:added': { gameObjectId: GameObjectId; componentId: ComponentId; typeId: string };
  'component:removed': { gameObjectId: GameObjectId; componentId: ComponentId; typeId: string };
  'component:enabled': { componentId: ComponentId };
  'component:disabled': { componentId: ComponentId };
  // Level events (renamed from scene)
  'level:loaded': { levelId: LevelId };
  'level:unloaded': { levelId: LevelId };
  'level:activated': { levelId: LevelId };
}

/**
 * Event listener type
 */
export type CoreEventListener<T extends CoreEventType> = (
  payload: CoreEventPayloads[T]
) => void;
